import { BrowserProtocol, createBasenameMiddleware, queryMiddleware } from 'farce';
import {
	type CreateRenderArgs,
	createFarceRouter,
	makeRouteConfig,
	Redirect,
	resolver,
} from 'found';
import React from 'react';

import AppPage from '../AppPage';
import { AI_TOOLS_TEMPLATE_SEGMENT } from '../components/AIAssistant/shared/templateSlugs';
import SchoolAdmin from '../components/SchoolAdmin/SchoolAdmin';
import SchoolAdminsList from '../components/SchoolAdmin/SchoolAdmins/SchoolAdminsList/SchoolAdminsList';
import SchoolDashboardPage from '../components/SchoolAdmin/SchoolDashboard';
import SchoolLicenseBillingPage from '../components/SchoolAdmin/SchoolLicenceBilling';
import LegacyTeachersRoute from '../components/StripeSchoolAdmin/LegacyTeachersRoute/LegacyTeachersRoute';
import StripeTeachersRoute from '../components/StripeSchoolAdmin/StripeTeachersRoute/StripeTeachersRoute';
import { type RouteDescriptor, routes } from '../constants';
import HistoryPage from '../routes/AIAssistantHistoryPage/HistoryPage';
import AIToolsPage from '../routes/AIToolsPage';
import ChangeEmailConfirm from '../routes/ChangeEmailConfirm';
import ChangeEmailPage from '../routes/ChangeEmailPage';
import CuratedCollectionItemListPage from '../routes/CuratedCollectionItemListPage';
import CuratedCollectionListPage from '../routes/CuratedCollectionListPage';
import CheckoutSuccessPage from '../routes/checkout/CheckoutSuccessPage';
import StripeCheckoutPage from '../routes/checkout/StripeCheckoutPage/StripeCheckoutPage';
import {
	CourseAttachmentsPage,
	CourseCoverPage,
	CourseFinishPage,
	CoursePlayerPage,
	CourseSearchPage,
	MyCertificatesPage,
	MyCoursesPage,
} from '../routes/courses'; // Also update coursePages in src/constants.ts
import NewDashboardPage from '../routes/DashboardPage';
import DownloadListPage from '../routes/DownloadListPage';
import Error404 from '../routes/Error404';
import FollowedCollectionItemListPage from '../routes/FollowedCollectionItemListPage';
import FollowedCollectionListPage from '../routes/FollowedCollectionListPage';
import KeycloakSSORedirect from '../routes/KeycloakSSORedirect/KeycloakSSORedirect';
import IpErrorPage from '../routes/login/IpRangeErrorPage';
import LoginPage from '../routes/login/LoginPage';
import NewResetPasswordConfirmedPage from '../routes/login/NewResetPasswordConfirmedPage';
import NewResetPasswordPage from '../routes/login/NewResetPasswordPage';
import NewSetPasswordPage from '../routes/login/NewSetPasswordPage';
import MyContentsPage from '../routes/MyContentsPage/MyContentsPage';
import OwnCollectionItemListPage from '../routes/OwnCollectionItemListPage';
import OwnCollectionListPage from '../routes/OwnCollectionListPage';
import OnboardingPage from '../routes/onboarding/OnboardingPage';
import PersonalDetailsPage from '../routes/PersonalDetailsPage';
import PinnedItemListPage from '../routes/PinnedItemListPage';
import ProductAttachmentsPage from '../routes/ProductAttachmentsPage';
import ProductPage from '../routes/ProductPage';
import PublicGeneratedDocumentPage from '../routes/PublicGeneratedDocumentPage';
import PublicVideoPlayerPage from '../routes/PublicVideoPlayerPage';
import QuickDownloadPage from '../routes/QuickDownload';
import ReaderPage from '../routes/ReaderPage';
import DeprecatedRegisterPremiumPage from '../routes/register/DeprecatedRegisterPremiumPage';
import SearchPage from '../routes/SearchPage';
import PaymentPage from '../routes/SinglePagePayment/PaymentPage';
import { PaymentMode } from '../routes/SinglePagePayment/PaymentPage/PaymentPage';
import PricingPage from '../routes/upgrade/PricingPage';
import StripeResubscribeCheckoutPage from '../routes/upgrade/StripeResubscribeCheckoutPage';
import StripeUpgradeConfirmPage from '../routes/upgrade/StripeUpgradeConfirmPage';
import StripeUpgradePage from '../routes/upgrade/StripeUpgradePage';
import AuthenticatedRoute from './AuthenticatedRoute';
import Route from './Route';
import RedirectIfLoggedIn from './redirect/RedirectIfLoggedIn';
import WalkInRestrictedRoute from './WalkInRestrictedRoute';

const getRouteConfig = (config: RouteDescriptor) => {
	let path = config.root;
	if (config.param) {
		path = `${path}/:${config.param}`;
	} else if (config.optionalParam) {
		path = `${path}/:${config.optionalParam}?`;
	}
	return {
		title: config.documentTitle,
		path,
	};
};

const getBackwardCompatibilityRedirects = () => (
	<>
		<Redirect
			from="/angebote/12fuer10"
			to={
				'/zahlungsdaten?data=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJsaWNlbmNlSWQiOjEzMn0' +
				'.IUKeDfyhOud0vwgDmr5sZGTSiNjH7IN6XeuCSxyG5ys&promoCode=schulstart22'
			}
		/>
		<Redirect
			from="/angebote/12fuer10-Ref"
			to={
				'/zahlungsdaten?data=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJsaWNlbmNlSWQiOjQxfQ' +
				'.VIzjPXGWBeOAvfKTVUNbOb0apCRon9uV7d4YxTeSMLE&promoCode=schulstart22-ref'
			}
		/>
	</>
);

const RouteContainer = (
	<Route
		path="/"
		Component={AppPage}
		render={({ Component, props }) => {
			// This 'render' property is being used to stop Found-router
			// from continuously un-mounting all of the apps components
			// on each route change (while navigation).
			// Hence, it shouldn't be removed.
			if (Component) return <Component {...props} />;
			return null;
		}}
	>
		<Redirect from="/" to={`/${routes.dashboard.root}`} />
		{getBackwardCompatibilityRedirects()}
		{/* Redirect deprecated Lernsets routes to dashboard (WOR-573) */}
		<Redirect
			from={`/${getRouteConfig(routes.learnsetsDashboard).path}*`}
			to={`/${routes.dashboard.root}`}
		/>
		<Redirect
			from={`/${getRouteConfig(routes.lernsetsPublicView).path}*`}
			to={`/${routes.dashboard.root}`}
		/>
		<Redirect
			from={`/${getRouteConfig(routes.lernsetsEditor).path}*`}
			to={`/${routes.dashboard.root}`}
		/>
		<Redirect
			from={`/${getRouteConfig(routes.lernsetsOverview).path}*`}
			to={`/${routes.dashboard.root}`}
		/>

		<AuthenticatedRoute>
			<WalkInRestrictedRoute>
				<Route
					path={`${routes.quickDownload.root}/:mindItemId`}
					title={routes.quickDownload.documentTitle}
					Component={QuickDownloadPage}
				/>
				<Route path={routes.collections.root}>
					<Route Component={OwnCollectionListPage} title={routes.collections.documentTitle} />
					<Route
						Component={OwnCollectionItemListPage}
						path=":collectionId"
						title={routes.collections.documentTitle}
					/>
				</Route>
				<Route {...getRouteConfig(routes.pinned)} Component={PinnedItemListPage} />
				<Route {...getRouteConfig(routes.downloads)} Component={DownloadListPage} />
				<Route path={routes.curatedCollections.root}>
					<Route Component={CuratedCollectionListPage} title={routes.curatedCollections.root} />
					<Route
						Component={CuratedCollectionItemListPage}
						path=":collectionId"
						title={routes.curatedCollections.documentTitle}
					/>
				</Route>
				<Route path={routes.followed.root}>
					<Route Component={FollowedCollectionListPage} title={routes.followed.documentTitle} />
					<Route
						Component={FollowedCollectionItemListPage}
						path=":collectionId"
						title={routes.followed.documentTitle}
					/>
				</Route>
				<Route {...getRouteConfig(routes.dashboard)} Component={NewDashboardPage} />
				<Route path={routes.aiTools.root} title={routes.aiTools.documentTitle} Component={AIToolsPage}>
					<Route path={`${AI_TOOLS_TEMPLATE_SEGMENT}/:templateSlug`} />
					<Route path={`:${routes.aiTools.optionalParam}?`} />
				</Route>
				<Route {...getRouteConfig(routes.aiAssistantHistory)} Component={HistoryPage} />
				<Route {...getRouteConfig(routes.personalDetails)} Component={PersonalDetailsPage} />
				<Route {...getRouteConfig(routes.courseCatalog)} Component={CourseSearchPage} />
				<Route {...getRouteConfig(routes.courseCover)} Component={CourseCoverPage} />
				<Route {...getRouteConfig(routes.coursePlayer)} Component={CoursePlayerPage} />
				<Route {...getRouteConfig(routes.myCertificates)} Component={MyCertificatesPage} />
				<Route {...getRouteConfig(routes.courseFinishPage)} Component={CourseFinishPage} />
				<Route {...getRouteConfig(routes.courseAttachments)} Component={CourseAttachmentsPage} />
				<Route {...getRouteConfig(routes.myCourses)} Component={MyCoursesPage} />
				<Route {...getRouteConfig(routes.myContents)} Component={MyContentsPage} />
			</WalkInRestrictedRoute>
			<Route {...getRouteConfig(routes.search)} Component={SearchPage} />
			<Route {...getRouteConfig(routes.productPage)} Component={ProductPage} />
			<Route
				title={routes.productAttachmentsPage.documentTitle}
				path={`${routes.productAttachmentsPage.root}/:productId/:attachmentsPath*`}
				Component={ProductAttachmentsPage}
			/>
			<Route path={`${routes.reader.root}/:documentId`} Component={ReaderPage} />
		</AuthenticatedRoute>
		<Route {...getRouteConfig(routes.login)} Component={LoginPage} />
		<Route {...getRouteConfig(routes.resetPassword)} Component={NewResetPasswordPage} />
		<Route {...getRouteConfig(routes.setPassword)} Component={NewSetPasswordPage} defer={true} />
		<Route
			{...getRouteConfig(routes.resetPasswordConfirmed)}
			Component={NewResetPasswordConfirmedPage}
		/>
		<Route {...getRouteConfig(routes.checkoutSuccess)} Component={CheckoutSuccessPage} />
		<Route
			{...getRouteConfig(routes.payment)}
			Component={(props) => (
				<RedirectIfLoggedIn
					basisUserRedirectTo={`/${routes.upgradePackageSelection.root}`}
					skipRedirectForBasisUser
				>
					<StripeCheckoutPage {...props} />
				</RedirectIfLoggedIn>
			)}
		/>
		<Route {...getRouteConfig(routes.setNewEmail)} Component={ChangeEmailPage} defer={true} />
		<Route
			{...getRouteConfig(routes.changeEmailConfirm)}
			Component={ChangeEmailConfirm}
			defer={true}
		/>
		<Route {...getRouteConfig(routes.ipRangeError)} Component={IpErrorPage} />
		<Route {...getRouteConfig(routes.keycloakRedirect)} Component={KeycloakSSORedirect} />
		<Route {...getRouteConfig(routes.onboardingPage)} Component={OnboardingPage} />
		<AuthenticatedRoute>
			<Route {...getRouteConfig(routes.upgradePackageSelection)}>
				<Route title={routes.upgradePackageSelection.documentTitle} Component={PricingPage} />
				<Redirect
					from={routes.deprecatedUpgradeRef.root}
					to={`/${routes.upgradePackageSelection.root}`}
				/>
			</Route>
			<Route {...getRouteConfig(routes.stripeUpgrade)}>
				<Route title={routes.stripeUpgrade.documentTitle} Component={StripeUpgradePage} />
				<Route {...getRouteConfig(routes.stripeUpgradeConfirm)}>
					<Route
						title={routes.stripeUpgradeConfirm.documentTitle}
						Component={StripeUpgradeConfirmPage}
					/>
					<Route
						{...getRouteConfig(routes.stripeUpgradeCheckout)}
						Component={StripeResubscribeCheckoutPage}
					/>
				</Route>
			</Route>
			<Redirect
				from={`${routes.deprecatedUpgradeOfferCode.root}*`}
				to={`/${routes.upgradePackageSelection.root}`}
			/>
			<Route
				{...getRouteConfig(routes.upgradePayment)}
				Component={(props) => <PaymentPage {...props} paymentMode={PaymentMode.PremiumUpgrade} />}
			/>
			<Redirect
				from={routes.deprecatedUpgradeConfirm.root}
				to={`/${routes.upgradePackageSelection.root}`}
			/>
		</AuthenticatedRoute>
		<Redirect
			from={routes.deprecatedPremiumTrialRegistration.root}
			to={(url) => {
				const {
					location: { query },
				} = url;
				return {
					pathname: `/${routes.register.root}/${routes.registerPremiumCreateAccount.root}`,
					query,
				};
			}}
		/>
		<Route {...getRouteConfig(routes.register)}>
			<Route
				{...getRouteConfig(routes.registerPremiumCreateAccount)}
				Component={(props) => (
					<RedirectIfLoggedIn basisUserRedirectTo={`/${routes.upgradePackageSelection.root}`}>
						<PaymentPage {...props} paymentMode={PaymentMode.PremiumRegistration} />
					</RedirectIfLoggedIn>
				)}
				defer={true}
			/>
			<Route
				{...getRouteConfig(routes.deprecatedRegisterPremiumPackageSelection)}
				Component={DeprecatedRegisterPremiumPage}
			/>
			<Route
				{...getRouteConfig(routes.deprecatedRegisterPremiumPayment)}
				Component={DeprecatedRegisterPremiumPage}
			/>
			<Route
				{...getRouteConfig(routes.deprecatedRegisterPremiumConfirm)}
				Component={DeprecatedRegisterPremiumPage}
			/>
		</Route>
		<Route path={`${routes.publicReader.root}/:accessToken`} Component={PublicVideoPlayerPage} />
		<Route
			{...getRouteConfig(routes.generatedDocumentPublicView)}
			Component={PublicGeneratedDocumentPage}
		/>
		<AuthenticatedRoute>
			<Route path="/school/dashboard" Component={SchoolAdmin}>
				<Route Component={SchoolDashboardPage} />
			</Route>
			<Route path="/school/admins" Component={SchoolAdmin}>
				<Route Component={SchoolAdminsList} />
			</Route>
			<Route path="/school/teachers" Component={SchoolAdmin}>
				<Route Component={LegacyTeachersRoute} />
			</Route>
			<Route path="/stripe-school/teachers" Component={SchoolAdmin}>
				<Route Component={StripeTeachersRoute} />
			</Route>
			<Route path="/school/license" Component={SchoolAdmin}>
				<Route Component={SchoolLicenseBillingPage} />
			</Route>
		</AuthenticatedRoute>
	</Route>
);

const routeConfig = makeRouteConfig(RouteContainer);

const renderError: CreateRenderArgs['renderError'] = ({ error }) => {
	return <AppPage>{error.status === 404 ? <Error404 /> : 'Got error, please refresh.'}</AppPage>;
};

const basenameMiddleware = createBasenameMiddleware({
	basename: process.env.BASENAME,
});

const FarceRouter = createFarceRouter({
	historyMiddlewares: [queryMiddleware, basenameMiddleware],
	historyProtocol: new BrowserProtocol(),
	renderError,
	routeConfig,
});

export default () => <FarceRouter resolver={resolver} />;
